<script setup>
// [TODO] Use Teleport to move the modal to the end of the body
import { ref, computed, onMounted } from 'vue';
import { useEventListener } from '@vueuse/core';
import Button from 'dashboard/components-next/button/Button.vue';

const { modalType, closeOnBackdropClick, onClose } = defineProps({
  closeOnBackdropClick: { type: Boolean, default: true },
  showCloseButton: { type: Boolean, default: true },
  onClose: { type: Function, default: null },
  fullWidth: { type: Boolean, default: false },
  modalType: { type: String, default: 'centered' },
  size: { type: String, default: '' },
});

const emit = defineEmits(['close']);
const show = defineModel('show', { type: Boolean, default: false });

const modalClassName = computed(() => {
  const modalClassNameMap = {
    centered: '',
    'right-aligned': 'right-aligned',
  };

  return `modal-mask skip-context-menu ${modalClassNameMap[modalType] || ''}`;
});

// [TODO] Revisit this logic to use outside click directive
const mousedDownOnBackdrop = ref(false);

const handleMouseDown = () => {
  mousedDownOnBackdrop.value = true;
};

const close = () => {
  show.value = false;
  emit('close');
  onClose?.();
};

const onMouseUp = () => {
  if (mousedDownOnBackdrop.value) {
    mousedDownOnBackdrop.value = false;
    if (closeOnBackdropClick) {
      close();
    }
  }
};

const onKeydown = e => {
  if (show.value && e.code === 'Escape') {
    close();
    e.stopPropagation();
  }
};

useEventListener(document.body, 'mouseup', onMouseUp);
useEventListener(document, 'keydown', onKeydown);

onMounted(() => {
  if (import.meta.env.DEV && onClose && typeof onClose === 'function') {
    // eslint-disable-next-line no-console
    console.warn(
      "[DEPRECATED] The 'onClose' prop is deprecated. Please use the 'close' event instead."
    );
  }
});
</script>

<template>
  <Teleport to="body">
    <transition name="modal-fade">
      <div
        v-if="show"
        :class="modalClassName"
        transition="modal"
        @mousedown="handleMouseDown"
      >
        <div
          role="dialog"
          aria-modal="true"
          class="relative max-h-full overflow-auto tic-solid-surface shadow-md modal-container rtl:text-right skip-context-menu"
          :class="{
            'rounded-xl w-[37.5rem]': !fullWidth,
            'items-center rounded-none flex h-full justify-center w-full':
              fullWidth,
            [size]: true,
          }"
          @mouse.stop
          @mousedown="event => event.stopPropagation()"
        >
          <Button
            v-if="showCloseButton"
            ghost
            slate
            icon="i-lucide-x"
            class="absolute z-10 ltr:right-2 rtl:left-2 top-2"
            @click="close"
          />
          <slot />
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style lang="scss">
.modal-mask {
  @apply flex items-center justify-center z-[9990] h-full left-0 fixed top-0 w-full;
  padding: 1rem;
  background: rgba(2, 6, 23, 0.78);
  backdrop-filter: none;
  -webkit-backdrop-filter: none;

  .modal-container {
    width: min(37.5rem, 100%);
    max-width: 100%;
    max-height: calc(100dvh - 2rem);
    overscroll-behavior: contain;

    &.medium {
      @apply max-w-[80%] w-[56.25rem];
    }

    // .content-box {
    //   @apply h-auto p-0;
    // }
    .content {
      @apply p-8;
    }

    form,
    .modal-content {
      @apply pt-4 pb-8 px-8 self-center;
      min-width: 0;

      a {
        @apply p-4;
      }

      .ProseMirror a {
        @apply p-0;
      }
    }
  }
}

.modal-big {
  @apply w-full;
}

.modal-mask.right-aligned {
  @apply justify-end;
  padding: 0;

  .modal-container {
    @apply rounded-none h-full w-[30rem];
    max-height: 100dvh;
  }
}

@media (max-width: 640px) {
  .modal-mask {
    padding: 0.75rem;

    .modal-container {
      max-height: calc(100dvh - 1.5rem);

      form,
      .modal-content {
        padding: 2.75rem 1rem 1.25rem;
      }
    }
  }
}

.modal-enter,
.modal-leave {
  @apply opacity-0;
}

.modal-enter .modal-container,
.modal-leave .modal-container {
  transform: scale(1.1);
}
</style>
